import { useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import { clearPrimary, listPublicPlans, queryPublicPlans } from '@wix/ambassador-pricing-plans-v2-plan/http';
import {
  PublicPlan,
  ListPublicPlansRequest,
  QueryPublicPlansRequest,
  QueryV2,
} from '@wix/ambassador-pricing-plans-v2-plan/types';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { ControllerFlowAPI, IHttpClient, useErrorHandler, useExperiments, useHttpClient } from '@wix/yoshi-flow-editor';
import { DEFAULT_PLAN_LIMIT } from '../constants';
import { memoize } from '../utils/fp';
import { wrapMethods } from '../utils/object';

type ErrorHandler = ControllerFlowAPI['errorHandler'];

export const usePlansApi = () => {
  const { experiments } = useExperiments();
  const isErrorHandlerEnabled = experiments.enabled(TPA_EXPERIMENTS.ERROR_HANDLER_TPA);
  const errorHandler = useErrorHandler();
  const httpClient = useHttpClient();

  const plansApi: PlansApi = useMemo(() => {
    if (isErrorHandlerEnabled) {
      return new PlansApiWithErrorHandler(httpClient, errorHandler);
    } else {
      return new PlansApiWithoutErrorHandler(httpClient);
    }
  }, [errorHandler, httpClient, isErrorHandlerEnabled]);

  return plansApi;
};

export function createPlansApi(flowAPI: ControllerFlowAPI): PlansApi {
  const isErrorHandlerEnabled = flowAPI.experiments.enabled(TPA_EXPERIMENTS.ERROR_HANDLER_TPA);

  if (isErrorHandlerEnabled) {
    return new PlansApiWithErrorHandler(flowAPI.httpClient, flowAPI.errorHandler);
  }

  return new PlansApiWithoutErrorHandler(flowAPI.httpClient);
}

export function memoizePlansApi(api: PlansApi, compare: (a: any[], b: any[]) => boolean = isEqual): PlansApi {
  return wrapMethods(api, (o, name) => memoize<any>(o[name].bind(o), compare));
}

type Filter = { exclude: string[] } | { include: string[] };
export type PlansQuery = Filter & { page?: number; limit?: number };

export interface PlansApi {
  loadPaidPlans(request?: ListPublicPlansRequest): Promise<PublicPlan[]>;
  query(query?: Partial<PlansQuery>): Promise<PublicPlan[]>;
  loadAllPaidPlans(): Promise<PublicPlan[]>;
  clearPrimaryPlan(): Promise<void>;
}

class PlansApiWithoutErrorHandler implements PlansApi {
  constructor(private httpClient: IHttpClient) {}

  async loadPaidPlans(request?: ListPublicPlansRequest): Promise<PublicPlan[]> {
    const defaultRequest = { limit: DEFAULT_PLAN_LIMIT };
    const response = await this.httpClient.request(listPublicPlans(request ?? defaultRequest));
    return response.data.plans || [];
  }

  async query(query: Partial<PlansQuery> = {}): Promise<PublicPlan[]> {
    const response = await this.httpClient.request(queryPublicPlans(plansApiQueryToRequest({ exclude: [], ...query })));
    return response.data.plans || [];
  }

  async loadAllPaidPlans(): Promise<PublicPlan[]> {
    const queryLimit = 100;
    const initialPageResponse = await this.httpClient.request(
      queryPublicPlans({ query: { paging: { limit: queryLimit } } }),
    );
    const totalPlanCount = initialPageResponse.data.pagingMetadata!.total!;
    const remainingPageCount = Math.floor(totalPlanCount / queryLimit);
    const remainingPageRequests = Array.from({ length: remainingPageCount }, (_, i) =>
      queryPublicPlans({ query: { paging: { limit: queryLimit, offset: (i + 1) * queryLimit } } }),
    );
    const remainingPageResponses = await Promise.all(
      remainingPageRequests.map((request) => this.httpClient.request(request)),
    );
    const firstPagePlans = initialPageResponse.data.plans || [];
    const remainingPagesPlans = remainingPageResponses.flatMap((response) => response.data.plans || []);
    return firstPagePlans.concat(remainingPagesPlans);
  }

  async clearPrimaryPlan() {
    await this.httpClient.request(clearPrimary({}));
  }
}

class PlansApiWithErrorHandler implements PlansApi {
  constructor(private httpClient: IHttpClient, private errorHandler: ErrorHandler) {}

  private defaultErrorCodesMap = {
    errorCodesMap: { applicationError: { PRICING_PLANS_NOT_INSTALLED: false }, validationError: {} },
  } as const;

  async loadPaidPlans(request?: ListPublicPlansRequest): Promise<PublicPlan[]> {
    const defaultRequest = { limit: DEFAULT_PLAN_LIMIT };
    const response = await this.errorHandler.withErrorHandler(
      () => this.httpClient.request(listPublicPlans(request ?? defaultRequest)),
      this.defaultErrorCodesMap,
    );
    return response.data.plans || [];
  }

  async query(query: Partial<PlansQuery> = {}): Promise<PublicPlan[]> {
    const response = await this.errorHandler.withErrorHandler(
      () => this.httpClient.request(queryPublicPlans(plansApiQueryToRequest({ exclude: [], ...query }))),
      this.defaultErrorCodesMap,
    );
    return response.data.plans || [];
  }

  async loadAllPaidPlans(): Promise<PublicPlan[]> {
    const queryLimit = 100;
    const initialPageResponse = await this.errorHandler.withErrorHandler(
      () => this.httpClient.request(queryPublicPlans({ query: { paging: { limit: queryLimit } } })),
      this.defaultErrorCodesMap,
    );
    const totalPlanCount = initialPageResponse.data.pagingMetadata!.total!;
    const remainingPageCount = Math.floor(totalPlanCount / queryLimit);
    const remainingPageRequests = Array.from({ length: remainingPageCount }, (_, i) =>
      queryPublicPlans({ query: { paging: { limit: queryLimit, offset: (i + 1) * queryLimit } } }),
    );
    const remainingPageResponses = await Promise.all(
      remainingPageRequests.map((request) =>
        this.errorHandler.withErrorHandler(() => this.httpClient.request(request), this.defaultErrorCodesMap),
      ),
    );
    const firstPagePlans = initialPageResponse.data.plans || [];
    const remainingPagesPlans = remainingPageResponses.flatMap((response) => response.data.plans || []);
    return firstPagePlans.concat(remainingPagesPlans);
  }

  async clearPrimaryPlan() {
    await this.errorHandler.withErrorHandler(
      () => this.httpClient.request(clearPrimary({})),
      this.defaultErrorCodesMap,
    );
  }
}

export function plansApiQueryToRequest(query: PlansQuery): QueryPublicPlansRequest {
  const limit = query.limit ? query.limit : DEFAULT_PLAN_LIMIT;
  return {
    query: {
      paging: {
        limit,
        offset: query.page ? query.page * limit : undefined,
      },
      filter: queryToFilter(query),
    },
  };
}

function queryToFilter(query: Filter): QueryV2['filter'] {
  if ('exclude' in query && query.exclude.length > 0) {
    return { $not: { id: { $in: query.exclude } } };
  }
  if ('include' in query && query.include.length > 0) {
    return { id: { $in: query.include } };
  }
}
